import { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import StatusPill from "@/components/StatusPill";
import { Radio, ArrowDown } from "lucide-react";

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.floor((now - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return `${d}d ago`;
}

/**
 * Cross-agent activity feed. Cursor-paginated via /agents/activity?before=<iso>.
 * Infinite scroll via IntersectionObserver on a sentinel <div> at the tail.
 */
export default function AgentActivityFeed() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const sentinelRef = useRef(null);

  const fetchMore = useCallback(async (before) => {
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get(`/agents/activity?limit=25${before ? `&before=${encodeURIComponent(before)}` : ""}`);
      setItems((prev) => (before ? [...prev, ...data.items] : data.items));
      setCursor(data.next_before);
      setHasMore(!!data.has_more);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMore(null); }, [fetchMore]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && cursor) fetchMore(cursor);
    }, { rootMargin: "200px" });
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [sentinelRef, cursor, hasMore, loading, fetchMore]);

  return (
    <section data-testid="agent-activity-feed" className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden flex flex-col">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#eef1f6] bg-[#fafbfc]">
        <Radio size={13} strokeWidth={2} className="text-[#0072bc]" />
        <div className="text-[11px] uppercase tracking-[.09em] text-[#0e1420] font-bold">Activity Feed</div>
        <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold ml-auto">
          {items.length} shown
        </div>
      </div>
      <div className="divide-y divide-[#eef1f6] max-h-[720px] overflow-y-auto">
        {items.map((it) => (
          <div key={it.id || `${it.agent_id}-${it.started_at}`} data-testid={`activity-item-${it.id}`}
               className="px-4 py-3 flex items-start gap-3">
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center text-white font-display font-bold text-[11.5px] shrink-0"
              style={{ background: it.avatar_color || "#94a3b8" }}
            >
              {it.initial || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-[13px] text-[#0e1420] truncate">{it.agent_name}</div>
                <StatusPill status={it.status} />
                <span className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)] ml-auto whitespace-nowrap">
                  {relTime(it.started_at)}
                </span>
              </div>
              {it.summary && (
                <div className="text-[12.5px] text-[#485563] mt-0.5 line-clamp-2">{it.summary}</div>
              )}
            </div>
          </div>
        ))}
        {!loading && items.length === 0 && !err && (
          <div className="px-4 py-6 text-center text-[13px] text-[color:var(--cc-ink-muted)]">No activity yet.</div>
        )}
        {err && (
          <div className="px-4 py-4 text-center text-[12.5px] text-[#991b1b]" data-testid="activity-feed-error">{err}</div>
        )}
        {loading && (
          <div className="px-4 py-3 text-center text-[11.5px] font-mono-data uppercase tracking-widest text-[color:var(--cc-ink-muted)]">
            Loading…
          </div>
        )}
        {/* Sentinel for infinite scroll */}
        {hasMore && !err && <div ref={sentinelRef} data-testid="activity-feed-sentinel" className="h-4" />}
        {!hasMore && items.length > 0 && (
          <div className="px-4 py-3 text-center text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-mono-data flex items-center justify-center gap-1.5">
            <ArrowDown size={11} /> End of activity
          </div>
        )}
      </div>
    </section>
  );
}
